import { FormSectionState } from '@navstation-portal/reporting/data-model/vessel-report';
import { SelectOption } from '@navstation-portal/shared/data-model/common';
import { NavMenuSection, NavStatus, UnitType } from '@navstation-portal/shared/data-model/ui';
import { OFFSHORE_EVENT_TYPES, OffshoreEventType } from './offshore.state';

export interface OffshoreVm {
  isFieldVisible: (tabId: string, sectionId: string, fieldId: string) => boolean;
  isFieldRequired: (tabId: string, sectionId: string, fieldId: string) => boolean;
  isFieldReadOnly: (tabId: string, sectionId: string, fieldId: string) => boolean;
  isSectionVisible: (tabId: string, sectionId?: string) => boolean;
  isReportConfigComplete: boolean;
  navSections: NavMenuSection[];
  validityMap: Record<string, NavStatus>;
  isFormValid: boolean;
}

export const DEFAULT_OFFSHORE_VM: OffshoreVm = {
  isFieldVisible: () => true,
  isFieldRequired: () => false,
  isFieldReadOnly: () => false,
  isSectionVisible: () => false,
  isReportConfigComplete: false,
  navSections: [
    {
      id: 'setup',
      label: 'Setup',
      status: 'pending',
      children: [
        {
          id: 'eventSelection',
          label: 'Event Selection',
          status: 'pending',
        },
      ],
    },
  ],
  validityMap: {},
  isFormValid: false,
} as const;

export type FormValueMap = {
  checkbox: boolean;
  number: number;
  text: string;
};

export const EVENT_OPTIONS: SelectOption[] = [
  { name: 'Anchor', code: OFFSHORE_EVENT_TYPES.ANCHOR },
  { name: 'Canal Transit', code: OFFSHORE_EVENT_TYPES.CANAL_TRANSIT },
  {
    name: 'Dynamic Positioning',
    code: OFFSHORE_EVENT_TYPES.DYNAMIC_POSITIONING,
  },
  { name: 'Drifting', code: OFFSHORE_EVENT_TYPES.DRIFTING },
  { name: 'Dry Dock / Repair', code: OFFSHORE_EVENT_TYPES.DRY_DOCK_REPAIR },
  { name: 'End of Sea Passage (Arrival)', code: OFFSHORE_EVENT_TYPES.ARRIVAL },
  { name: 'Maneuvering', code: OFFSHORE_EVENT_TYPES.MANEUVERING },
  { name: 'Port', code: OFFSHORE_EVENT_TYPES.PORT },
  { name: 'Sea', code: OFFSHORE_EVENT_TYPES.SEA },
  {
    name: 'Standby on Location',
    code: OFFSHORE_EVENT_TYPES.STANDBY_ON_LOCATION,
  },
  {
    name: 'Start of Sea Passage (Departure)',
    code: OFFSHORE_EVENT_TYPES.DEPARTURE,
  },
];

export const getNameFromEventCode = (code: string): string | undefined =>
  EVENT_OPTIONS.find((option) => option.code === code)?.name;

export const getCodeFromEventName = (name: string | undefined): string | unknown =>
  EVENT_OPTIONS.find((option) => option.name === name)?.code;

export const getSelectOptionFromEventCode = (eventType: string | undefined): SelectOption | undefined =>
  EVENT_OPTIONS.find((option) => option.code === eventType);

export interface CategorisedSelectOption extends SelectOption {
  categories?: OffshoreEventType[];
}

export const OPERATION_OPTIONS = [
  { name: 'Anchor Handling', code: 'anchor_handling' },
  {
    name: 'Bunkering / Debunkering',
    code: 'bunkering_debunkering',
    categories: [
      OFFSHORE_EVENT_TYPES.ANCHOR,
      OFFSHORE_EVENT_TYPES.DYNAMIC_POSITIONING,
      OFFSHORE_EVENT_TYPES.DRIFTING,
      OFFSHORE_EVENT_TYPES.DRY_DOCK_REPAIR,
      OFFSHORE_EVENT_TYPES.PORT,
      OFFSHORE_EVENT_TYPES.STANDBY_ON_LOCATION,
    ],
  },
  { name: 'Cable Laying', code: 'cable_laying' },
  {
    name: 'Cargo',
    code: 'cargo',
    categories: [OFFSHORE_EVENT_TYPES.PORT],
  },
  { name: 'Diving', code: 'diving' },
  {
    name: 'Embarkation/Disembarkation',
    code: 'embarkation_disembarkation',
    categories: [OFFSHORE_EVENT_TYPES.PORT],
  },
  { name: 'Idling', code: 'idling' },
  { name: 'Installation', code: 'installation' },
  { name: 'Jacking', code: 'jacking' },
  { name: 'Lifting', code: 'lifting' },
  {
    name: 'Maintenance and Repair',
    code: 'maintenance_and_repair',
  },
  { name: 'Others', code: 'others' },
  { name: 'Pipe Laying', code: 'pipe_laying' },
  { name: 'ROV', code: 'rov' },
  { name: 'Towing', code: 'towing' },
  { name: 'Trenching', code: 'trenching' },
] as const satisfies CategorisedSelectOption[];

export const getNameFromOperationCode = (code: string | undefined): string | undefined =>
  OPERATION_OPTIONS.find((option) => option.code === code)?.name;

export const getCodeFromOperationName = (name: string | undefined): string | unknown =>
  OPERATION_OPTIONS.find((option) => option.name === name)?.code;

export const getSelectOptionFromOperationCode = (operationType: string | undefined): SelectOption | undefined =>
  OPERATION_OPTIONS.find((option) => option.code === operationType);

export interface FormConfigItem<T extends keyof FormValueMap = keyof FormValueMap> {
  label: string;
  description?: string;
  type: T;
  id: string;
  required?: boolean;
  readonly?: boolean;
  disabled?: boolean;
  min?: number;
  max?: number;
  labelWidthPercentage?: number;
  unit?: UnitType;
}

export type StatusResolver = () => NavStatus | undefined | 'hidden';
export interface NavItem {
  id: string;
  label: string;
  status?: NavStatus;
}
export interface TabNav {
  id: string;
  label: string;
  status: NavStatus;
  children: NavItem[];
}

export interface NavModel {
  navSections: NavMenuSection[];
  validityMap: Record<string, NavStatus>;
}

export interface NavItemConfig extends NavItem {
  fieldId?: string;
  statusResolver?: StatusResolver;
}

export interface SectionTemplateItem {
  tabId: string;
  label?: string;
  sectionId: string;
  navItems: NavItem[];
}

export interface SectionTemplateItemConfig extends Omit<SectionTemplateItem, 'navItems'> {
  statusResolver?: StatusResolver;
  navItems?: NavItemConfig[];
}

export type SectionStateRegistry = Record<string, Record<string, () => FormSectionState | undefined>>;
